/* cart-custom.js – toast, contador, meta de frete grátis, fechar drawer */
(function () {
  const originalFetch = window.fetch.bind(window);
  const root = () => window.Shopify?.routes?.root || "/";
  let internalRequest = false;
  let syncPromise = null;
  let queuedSyncRequest = null;
  let refreshTimer = null;
  let initialSyncTimer = null;

  const getConfig = () => window.AdegaAutoGiftConfig || {};
  const toNumber = (value) => parseInt(value, 10) || 0;
  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "BRL",
    }).format((Number(cents) || 0) / 100);
  };
  const getRules = () => {
    const config = getConfig();
    const sourceRules = Array.isArray(config.rules)
      ? config.rules
      : [
          {
            id: "legacy",
            triggerType: config.triggerType || "product",
            triggerProductId: config.triggerProductId,
            triggerCollectionHandle: config.triggerCollectionHandle,
            triggerCollectionProductIds: config.triggerCollectionProductIds,
            triggerQuantity: config.triggerQuantity || 1,
            giftProductId: config.giftProductId,
            giftVariantId: config.giftVariantId,
            giftQuantity: config.giftQuantity || 1,
          },
        ];
    const usedTriggerKeys = new Set();
    const usedGiftVariantIds = new Set();

    return sourceRules
      .map((rule, index) => {
        const triggerType = ["collection", "cart_total", "collection_total"].includes(rule.triggerType) ? rule.triggerType : "product";
        const triggerCollectionProductIds = Array.isArray(rule.triggerCollectionProductIds)
          ? rule.triggerCollectionProductIds.map(toNumber).filter(Boolean)
          : [];

        return {
          id: String(rule.id || index + 1),
          triggerType,
          triggerProductId: toNumber(rule.triggerProductId),
          triggerCollectionHandle: String(rule.triggerCollectionHandle || rule.collectionHandle || ""),
          triggerCollectionProductIds,
          triggerQuantity: Math.max(1, toNumber(rule.triggerQuantity || rule.quantity || 1)),
          triggerAmount: Math.max(0, toNumber(rule.triggerAmount || rule.amount || 0)),
          giftProductId: toNumber(rule.giftProductId),
          giftVariantId: toNumber(rule.giftVariantId),
          giftQuantity: Math.max(1, toNumber(rule.giftQuantity || 1)),
        };
      })
      .filter((rule) => {
        if (!rule.giftVariantId) return false;
        if (rule.triggerType === "product" && !rule.triggerProductId) return false;
        if (rule.triggerType === "collection" && !rule.triggerCollectionProductIds.length) return false;
        if (rule.triggerType === "collection_total" && (!rule.triggerCollectionProductIds.length || !rule.triggerAmount)) return false;
        if (rule.triggerType === "cart_total" && !rule.triggerAmount) return false;

        let triggerKey = `product:${rule.triggerProductId}`;

        if (rule.triggerType === "collection") {
          triggerKey = `collection:${rule.triggerCollectionHandle || rule.triggerCollectionProductIds.join(",")}`;
        } else if (rule.triggerType === "collection_total") {
          triggerKey = `collection_total:${rule.triggerCollectionHandle || rule.triggerCollectionProductIds.join(",")}:${rule.triggerAmount}`;
        } else if (rule.triggerType === "cart_total") {
          triggerKey = `cart_total:${rule.triggerAmount}`;
        }

        if (usedTriggerKeys.has(triggerKey) || usedGiftVariantIds.has(rule.giftVariantId)) return false;

        usedTriggerKeys.add(triggerKey);
        usedGiftVariantIds.add(rule.giftVariantId);

        return true;
      });
  };
  const isEnabled = () => {
    const config = getConfig();
    const rules = getRules();

    return Boolean(config.enabled && rules.length);
  };
  const isCartObject = (cart) => Boolean(cart && Array.isArray(cart.items));

  const requestJson = (path, options = {}) => {
    internalRequest = true;

    return originalFetch(root() + path.replace(/^\//, ""), options)
      .then((response) => response.json())
      .finally(() => {
        internalRequest = false;
      });
  };

  const postJson = (path, body) =>
    requestJson(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    });

  const isAutoGiftLine = (item) => {
    const properties = item.properties || {};

    return properties._auto_gift === "true";
  };

  const isGiftVariantLine = (item, rule) => toNumber(item.id) === rule.giftVariantId;

  const getAutoGiftRuleId = (item) => String((item.properties || {})._auto_gift_rule_id || "");

  const findRuleForAutoGiftLine = (item, rules) => {
    const ruleId = getAutoGiftRuleId(item);

    return rules.find((rule) => rule.id === ruleId) || rules.find((rule) => toNumber(item.id) === rule.giftVariantId);
  };

  const getTriggerQuantity = (cart, rule) => {
    if (rule.triggerType === "cart_total" || rule.triggerType === "collection_total") {
      return getEligibleCartSubtotal(cart, rule);
    }

    return cart.items.reduce((quantity, item) => {
      if (isAutoGiftLine(item)) return quantity;
      if (isGiftVariantLine(item, rule)) return quantity;
      if (rule.triggerType === "collection") {
        if (!rule.triggerCollectionProductIds.includes(toNumber(item.product_id))) return quantity;
      } else if (toNumber(item.product_id) !== rule.triggerProductId) {
        return quantity;
      }

      return quantity + toNumber(item.quantity);
    }, 0);
  };

  const getEligibleCartSubtotal = (cart, rule) =>
    cart.items.reduce((subtotal, item) => {
      if (isAutoGiftLine(item)) return subtotal;
      if (isGiftVariantLine(item, rule)) return subtotal;

      if (rule.triggerType === "collection_total") {
        const itemCollectionHandles = String(item.properties?._auto_gift_collection_handles || "")
          .split(",")
          .map((handle) => handle.trim())
          .filter(Boolean);
        const belongsToCollection =
          rule.triggerCollectionProductIds.includes(toNumber(item.product_id)) ||
          itemCollectionHandles.includes(rule.triggerCollectionHandle);

        if (!belongsToCollection) {
          return subtotal;
        }
      }

      return subtotal + toNumber(item.final_line_price ?? item.line_price ?? item.original_line_price);
    }, 0);

  const getDesiredGiftQuantity = (triggerCartQuantity, rule) => {
    if (rule.triggerType === "cart_total" || rule.triggerType === "collection_total") {
      return triggerCartQuantity >= rule.triggerAmount ? rule.giftQuantity : 0;
    }

    const applications = Math.floor(triggerCartQuantity / rule.triggerQuantity);

    return applications * rule.giftQuantity;
  };

  const runSync = async (cart, options = {}) => {
    const rules = getRules();
    let changed = false;
    let currentCart = cart || (await requestJson("cart.js"));

    if (!isEnabled()) {
      window.AdegaAutoGiftLastResult = {
        enabled: false,
        rules,
        changed,
      };

      return { cart: currentCart, changed };
    }

    const activeRuleIds = new Set();
    const autoGiftLines = currentCart.items.filter(isAutoGiftLine);
    const handledAutoGiftLineKeys = new Set();

    window.AdegaAutoGiftLastResult = {
      enabled: true,
      mode: options.checkout === true ? "checkout" : "cart",
      rules: rules.map((rule) => ({
        ...rule,
        triggerCartQuantity: getTriggerQuantity(currentCart, rule),
        desiredGiftQuantity: getDesiredGiftQuantity(getTriggerQuantity(currentCart, rule), rule),
      })),
      autoGiftLineCount: autoGiftLines.length,
      changed: false,
    };

    for (const rule of rules) {
      const triggerCartQuantity = getTriggerQuantity(currentCart, rule);
      const desiredGiftQuantity = getDesiredGiftQuantity(triggerCartQuantity, rule);
      const ruleAutoGiftLines = autoGiftLines.filter((item) => findRuleForAutoGiftLine(item, rules)?.id === rule.id);
      const ruleGiftVariantLines = currentCart.items.filter((item) => isGiftVariantLine(item, rule));

      if (desiredGiftQuantity > 0) {
        activeRuleIds.add(rule.id);

        if (ruleGiftVariantLines.length === 0) {
          await postJson("cart/add.js", {
            id: rule.giftVariantId,
            quantity: desiredGiftQuantity,
            properties: {
              _auto_gift: "true",
              _auto_gift_rule_id: rule.id,
              _auto_gift_trigger_type: rule.triggerType,
              _auto_gift_trigger_product_id: String(rule.triggerProductId || ""),
              _auto_gift_trigger_collection_handle: rule.triggerCollectionHandle,
              _auto_gift_trigger_amount: String(rule.triggerAmount || ""),
            },
          });
          changed = true;
          window.AdegaAutoGiftLastResult.changed = true;
        } else {
          const primaryGiftLine = ruleAutoGiftLines[0] || ruleGiftVariantLines[0];

          for (const item of ruleGiftVariantLines) {
            const desiredQuantity = item.key === primaryGiftLine.key ? desiredGiftQuantity : 0;
            const changeBody = {
              id: item.key,
              quantity: desiredQuantity,
            };

            if (item.key === primaryGiftLine.key && !isAutoGiftLine(item)) {
              changeBody.properties = {
                _auto_gift: "true",
                _auto_gift_rule_id: rule.id,
                _auto_gift_trigger_type: rule.triggerType,
                _auto_gift_trigger_product_id: String(rule.triggerProductId || ""),
                _auto_gift_trigger_collection_handle: rule.triggerCollectionHandle,
                _auto_gift_trigger_amount: String(rule.triggerAmount || ""),
              };
            }

            if (item.quantity !== desiredQuantity) {
              await postJson("cart/change.js", changeBody);
              handledAutoGiftLineKeys.add(item.key);
              changed = true;
              window.AdegaAutoGiftLastResult.changed = true;
            } else if (changeBody.properties) {
              await postJson("cart/change.js", changeBody);
              handledAutoGiftLineKeys.add(item.key);
              changed = true;
              window.AdegaAutoGiftLastResult.changed = true;
            }
          }
        }
      } else if (ruleAutoGiftLines.length > 0) {
        for (const item of ruleAutoGiftLines) {
          await postJson("cart/change.js", {
            id: item.key,
            quantity: 0,
          });
          handledAutoGiftLineKeys.add(item.key);
          changed = true;
          window.AdegaAutoGiftLastResult.changed = true;
        }
      }
    }

    const orphanAutoGiftLines = autoGiftLines.filter((item) => {
      if (handledAutoGiftLineKeys.has(item.key)) return false;

      const rule = findRuleForAutoGiftLine(item, rules);

      return !rule || !activeRuleIds.has(rule.id);
    });

    for (const item of orphanAutoGiftLines) {
      await postJson("cart/change.js", {
        id: item.key,
        quantity: 0,
      });
      handledAutoGiftLineKeys.add(item.key);
      changed = true;
      window.AdegaAutoGiftLastResult.changed = true;
    }

    if (changed) {
      currentCart = await requestJson("cart.js");
    }

    return { cart: currentCart, changed };
  };

  const runQueuedSync = async (cart, options = {}) => {
    let result = await runSync(cart, options);

    while (queuedSyncRequest) {
      const nextRequest = queuedSyncRequest;
      queuedSyncRequest = null;
      const nextResult = await runSync(nextRequest.cart, nextRequest.options);

      result = {
        ...nextResult,
        changed: result.changed || nextResult.changed,
      };
    }

    return result;
  };

  const sync = (cart, options = {}) => {
    if (!isEnabled()) return Promise.resolve({ cart, changed: false });
    if (syncPromise) {
      queuedSyncRequest = { cart, options };

      return syncPromise;
    }

    syncPromise = runQueuedSync(cart, options)
      .catch((error) => {
        console.error("Auto gift sync failed", error);
        window.AdegaAutoGiftLastResult = {
          ...(window.AdegaAutoGiftLastResult || {}),
          error: error?.message || String(error),
        };

        return { cart, changed: false, error };
      })
      .finally(() => {
        syncPromise = null;
      });

    return syncPromise;
  };

  const getSectionInnerHTML = (html, selector = ".shopify-section") =>
    new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML || "";

  const refreshCartSurfaces = async (cartOverride) => {
    const sections = ["cart-notification-content", "cart-notification-crosssell-products", "cart-icon-bubble"];
    const mainCart = document.getElementById("main-cart-items");
    const mainCartSectionId = mainCart?.dataset?.id;

    if (mainCartSectionId) {
      sections.push(mainCartSectionId);
    }

    const sectionHtml = await requestJson(
      `?sections=${sections.join(",")}&sections_url=${encodeURIComponent(window.location.pathname)}`
    );
    const cartNotification = document.querySelector("cart-notification");

    if (cartNotification && typeof cartNotification.updateContent === "function") {
      cartNotification.updateContent(sectionHtml);
    }

    if (mainCartSectionId && sectionHtml[mainCartSectionId] && mainCart) {
      const updatedCartContent = getSectionInnerHTML(sectionHtml[mainCartSectionId], ".checkout-content-left");
      const currentCartContent = mainCart.querySelector(".checkout-content-left");

      if (updatedCartContent && currentCartContent) {
        currentCartContent.innerHTML = updatedCartContent;
      }
    }

    if (sectionHtml["cart-icon-bubble"]) {
      const currentBubble = document.getElementById("shopify-section-cart-icon-bubble");

      if (currentBubble) {
        currentBubble.innerHTML = getSectionInnerHTML(sectionHtml["cart-icon-bubble"]);
      }
    }

    const cart = isCartObject(cartOverride) ? cartOverride : await requestJson("cart.js");
    document.querySelectorAll(".checkout-sidebar .cart-subtotal span").forEach((subtotal) => {
      subtotal.textContent = ` ${formatMoney(cart.total_price)}`;
    });
    window.updateAdegaFreeShippingGoal?.(cart);
    window.cartCount?.(cart);
    window.renderCrosssellProducts?.(sectionHtml);

    return cart;
  };

  const syncAndRefresh = async (cart, options = {}) => {
    const result = await sync(cart, options);

    if (result.changed) {
      result.cart = await refreshCartSurfaces(result.cart);
    }

    return result;
  };

  const scheduleSyncAndRefresh = (cart, delay = 120) => {
    if (!isEnabled()) return;

    clearTimeout(initialSyncTimer);
    initialSyncTimer = null;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      syncAndRefresh(isCartObject(cart) ? cart : undefined);
    }, delay);
  };

  const isCartMutation = (resource, options = {}) => {
    const method = (options.method || "GET").toUpperCase();
    const url = typeof resource === "string" ? resource : resource?.url || "";

    return method !== "GET" && /\/cart\/(add|change|update)(?:\.js)?(?:[?#]|$)/.test(url);
  };

  const isCartStateMutation = (resource) => {
    const url = typeof resource === "string" ? resource : resource?.url || "";

    return /\/cart\/(change|update)(?:\.js)?(?:[?#]|$)/.test(url);
  };

  window.fetch = function (resource, options) {
    return originalFetch(resource, options).then((response) => {
      if (!internalRequest && isCartMutation(resource, options)) {
        if (isCartStateMutation(resource)) {
          response
            .clone()
            .json()
            .then((cart) => {
              if (isCartObject(cart)) {
                scheduleSyncAndRefresh(cart);
              }
            })
            .catch(() => {});
        } else {
          scheduleSyncAndRefresh(undefined, 180);
        }
      }

      return response;
    });
  };

  const isCartPage = () => window.location.pathname === window.routes?.cart_url || window.location.pathname === "/cart";

  const initAutoGiftSync = () => {
    if (window.jQuery) {
      jQuery(document).ajaxSuccess((event, xhr, settings) => {
        if (!internalRequest && /\/cart\/(add|change|update)(?:\.js)?(?:[?#]|$)/.test(settings.url || "")) {
          scheduleSyncAndRefresh();
        }
      });
    }

    const initialSyncDelay = window.freeShippingConfig?.template === "cart" ? 120 : 1200;
    initialSyncTimer = setTimeout(() => {
      initialSyncTimer = null;
      syncAndRefresh();
    }, initialSyncDelay);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAutoGiftSync);
  } else {
    initAutoGiftSync();
  }

  window.addEventListener("pageshow", () => {
    if (isCartPage()) {
      scheduleSyncAndRefresh(undefined, 0);
    }
  });

  const goToCheckout = async (button) => {
    if (!isEnabled()) return false;

    button?.setAttribute("disabled", "disabled");
    button?.classList.add("loading");

    await syncAndRefresh(undefined, { checkout: true });
    window.location.href = root() + "checkout";

    return true;
  };

  document.addEventListener(
    "click",
    (event) => {
      const checkoutButton = event.target.closest('button[name="checkout"], input[name="checkout"]');

      if (!checkoutButton || checkoutButton.disabled || !isEnabled()) return;

      event.preventDefault();
      goToCheckout(checkoutButton);
    },
    true
  );

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;

      if (!form?.matches?.("#cart, #form-minicart") || !isEnabled()) return;

      event.preventDefault();
      goToCheckout(form.querySelector('[name="checkout"]'));
    },
    true
  );

  window.AdegaAutoGift = {
    sync,
    syncAndRefresh,
    refreshCartSurfaces,
  };
})();

(function () {
  const toNumber = (value) => Number(value) || 0;
  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "BRL",
    }).format(cents / 100);
  };

  const getGoalCents = () => {
    const config = window.freeShippingConfig || {};
    const rate = toNumber(window.Shopify?.currency?.rate) || 1;

    return Math.round(toNumber(config.goal_amt) * rate * 100);
  };

  const render = (cart) => {
    const config = window.freeShippingConfig || {};
    const targets = document.querySelectorAll("[data-free-shipping-goal-section]");
    const goalCents = getGoalCents();

    if (!config.enable || !goalCents || !cart || !targets.length) {
      return;
    }

    const subtotal = toNumber(cart.total_price);
    const progress = subtotal > 0 ? Math.min(100, Math.round((subtotal * 100) / goalCents)) : 0;
    const amount = Math.max(0, goalCents - subtotal);
    const message =
      subtotal >= goalCents
        ? config.goal_amt_msg_2 || ""
        : (config.goal_amt_msg_1 || "").replace("{{amount}}", formatMoney(amount));

    const html =
      progress > 0 && message
        ? `<div data-item="freeshipping-goal" class="cart-goal">
            <div class="cart-goal-msg">${message}</div>
            <div class="cart-goal__inner">
              <div class="cart-goal__bar">
                <span style="--progress: ${progress}%"><span class="goal-badge">${progress}</span></span>
              </div>
            </div>
          </div>`
        : "";

    targets.forEach((target) => {
      target.innerHTML = html;
    });
  };

  window.updateAdegaFreeShippingGoal = render;

  const renderInitialCartGoal = () => {
    const hasServerRenderedGoal = document.querySelector(
      "[data-free-shipping-goal-section] [data-item='freeshipping-goal']"
    );

    if (hasServerRenderedGoal) return;

    render({ total_price: toNumber(window.freeShippingConfig?.cart_total) });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", renderInitialCartGoal);
  } else {
    renderInitialCartGoal();
  }
})();

document.addEventListener("DOMContentLoaded", () => {
  /* ---------- Toast ---------- */
  const toast = document.getElementById("add-to-cart-toast");
  const msgBox = toast.querySelector(".toast-message");
  const goalBox = toast.querySelector('[data-item="freeshipping-goal"]');

  const showToast = () => {
    toast.classList.remove("d-none", "fade-out");
    toast.style.opacity = "1";
    msgBox.style.opacity = "1";
    
    setTimeout(()=> toast.classList.add('fade-out'), 5000);
  };

  /* ---------- Free-shipping Goal ---------- */
  const normaliseCents = (v) => (v < 1000 ? v * 100 : v); // “150” → “15000”

  const updateFreeShippingGoal = (cart) => {
    if (!goalBox) return;

    /* --- valores em centavos --- */
    let goal = parseInt(goalBox.dataset.goal, 10) || 0;
    goal = normaliseCents(goal);
    if (goal === 0) return; // meta não configurada

    const subtotal = cart.total_price;
    const needMore = Math.max(0, goal - subtotal); // nunca negativo

    /* --- elementos --- */
    const msg1 = goalBox.querySelector(".msg-1");
    const msg2 = goalBox.querySelector(".msg-2");
    const bar = goalBox.querySelector(".cart-goal__bar > span");
    const badge = goalBox.querySelector(".goal-badge");

    /* --- guarda o template original apenas 1x --- */
    if (!msg1.dataset.template) msg1.dataset.template = msg1.innerHTML;

    /* --- substitui todos placeholders possíveis --- */
    const formatted = Shopify.formatMoney(needMore);
    msg1.innerHTML = msg1.dataset.template
      .replace(/{{\s*amount\s*}}/gi, formatted)
      .replace(/\[amount]/gi, formatted)
      .replace(/{{\s*valor\s*}}/gi, formatted)
      .replace(/\[valor]/gi, formatted);

    /* --- barra de progresso --- */
    const pct = Math.min(100, Math.round((subtotal / goal) * 100));
    bar.style.width = pct + "%";

    /***** badge “%” *****/
    if (badge) {
      badge.textContent = pct; // texto
      badge.dataset.progress = pct + "%"; // se CSS usa data-progress
    }

    /* --- alterna mensagens --- */
    if (needMore > 0) {
      msg1.classList.remove("d-none");
      msg2.classList.add("d-none");
    } else {
      msg1.classList.add("d-none");
      msg2.classList.remove("d-none");
    }

    goalBox.classList.remove("d-none"); // garante visível
  };

  /* ---------- Cabeçalho ---------- */
  const updateHeaderCart = (cart) => {
    const show = cart.item_count > 0;
    document
      .querySelectorAll("[data-cart-count], .cart-icon .count")
      .forEach((el) => {
        el.textContent = cart.item_count;
        el.setAttribute("data-cart-count", cart.item_count);
        el.classList.toggle("d-none", !show);
        el.style.display = show ? "" : "none";
      });

    document
      .querySelectorAll(".cart-total-header")
      .forEach(
        (el) => (el.textContent = Shopify.formatMoney(cart.total_price))
      );

    updateFreeShippingGoal(cart); // barra + mensagens
  };

  /* ---------- Add-to-Cart ---------- */
  document.addEventListener("click", async (e) => {
    const btn = e.target.closest(".js-add-to-cart, [data-trigger='addtocart']");
    if (!btn) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    const form = btn.closest("form");
    const formVariantId = form?.querySelector('[name="id"]')?.value;
    const formQuantity = form?.querySelector('[name="quantity"]')?.value;
    const id = btn.dataset.variantId || btn.dataset.variant || formVariantId;
    const qty = btn.dataset.qty || formQuantity || 1;
    const productCollections = String(btn.dataset.productCollections || "").trim();
    const item = { id, quantity: qty };
    const redirectToCart = btn.closest("product-form")?.dataset.redirectToCart === "true";
    const shouldOpenCartNotification = btn.matches("[data-trigger='addtocart']");

    if (!id || id === "undefined") return;

    if (productCollections) {
      item.properties = {
        _auto_gift_collection_handles: productCollections,
      };
    }

    try {
      const add = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(item),
      });
      if (!add.ok) throw new Error(await add.text());

      const autoGiftResult =
        typeof window.AdegaAutoGift?.syncAndRefresh === "function"
          ? await window.AdegaAutoGift.syncAndRefresh()
          : { changed: false };

      if (redirectToCart) {
        window.location.href = window.routes?.cart_url || "/cart";
        return;
      }

      const cart = autoGiftResult?.cart || (await fetch("/cart.js").then((r) => r.json()));
      if (shouldOpenCartNotification) {
        if (!autoGiftResult?.changed && typeof window.AdegaAutoGift?.refreshCartSurfaces === "function") {
          await window.AdegaAutoGift.refreshCartSurfaces(cart);
        }
        document.querySelector("cart-notification")?.open(true);
        return;
      }

      updateHeaderCart(cart);
      showToast();
    } catch (err) {
      if (btn.href) window.location.href = btn.href; // fallback
    }
  }, true);

  document.addEventListener("submit", async (e) => {
    const form = e.target;

    if (!form?.matches?.("#product-form-quickview")) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const submitButton = form.querySelector('[type="submit"]');
    const cartNotification = document.querySelector("cart-notification");
    const formData = new FormData(form);

    try {
      submitButton?.setAttribute("disabled", "disabled");
      submitButton?.classList.add("loading");

      const add = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: formData,
      });

      if (!add.ok) {
        const error = await add.json().catch(() => ({}));
        throw new Error(error.description || window.cartStrings?.error || "Erro ao adicionar produto.");
      }

      const autoGiftResult = await window.AdegaAutoGift?.syncAndRefresh?.();
      if (!autoGiftResult?.changed && typeof window.AdegaAutoGift?.refreshCartSurfaces === "function") {
        await window.AdegaAutoGift.refreshCartSurfaces(autoGiftResult?.cart);
      }
      window.jQuery?.magnificPopup?.close?.();
      cartNotification?.open(true);
    } catch (err) {
      const errorTarget = document.querySelector(".errormsg-quickview");

      if (errorTarget) {
        errorTarget.textContent = err.message;
        errorTarget.style.display = "";
      }
    } finally {
      submitButton?.removeAttribute("disabled");
      submitButton?.classList.remove("loading");
    }
  }, true);

  /* ---------- Fecha drawer automático ---------- */
  const minicart = document.getElementById("cart-notification");
  if (minicart) {
    new MutationObserver(() => {
      minicart.style.display = "none";
    }).observe(minicart, { attributes: true, childList: true });
  }
});
